import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { openCommandPalette } from '../../components/CommandPalette/commandPaletteStore';
import { smlNavigationChromeMessages as messages } from '../smlNavigationMessages';
import { placeRailTip } from '../SidebarNavItem';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole sidebar; each part uses its own slice of it */
import styles from '../../SMLSideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The key hint of the palette: ⌘K on a Mac, Ctrl K elsewhere. */
function shortcutHint(): string {
  const platform = typeof navigator === 'undefined' ? '' : navigator.platform;
  return /Mac|iPhone|iPad/.test(platform) ? '⌘K' : 'Ctrl K';
}

interface SidebarSearchFieldProps {
  /** The Search page (`/app/browse`) is on screen. No menu item is lit there; this field is. */
  isActive: boolean;
}

/**
 * design: navigation-v2 1a (nav2.css .srch). It looks like a field but is a button: it opens the
 * command palette, the search of the app. On the rail it is an icon with a tooltip.
 */
const SidebarSearchFieldComponent: React.FC<SidebarSearchFieldProps> = ({ isActive }) => {
  const intl = useIntl();
  const label = intl.formatMessage(messages.search);

  return (
    <div className={styles.searchSlot} onMouseEnter={placeRailTip} onFocus={placeRailTip}>
      <button
        type="button"
        className={clsx(styles.search, isActive && styles.searchActive)}
        onClick={openCommandPalette}
        aria-haspopup="dialog"
        aria-current={isActive ? 'page' : undefined}
        data-testid="sidebar-search"
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
        <span className={styles.label}>{label}</span>
        <kbd className={styles.kbd} aria-hidden="true">
          {shortcutHint()}
        </kbd>
        <span className={styles.tip} aria-hidden="true">
          {label}
        </span>
      </button>
    </div>
  );
};

export const SidebarSearchField = TypedMemo(SidebarSearchFieldComponent);
